<template>
    <div class="filter-container">
        <md-button class="md-primary md-just-icon md-round filter-button" @click="showFilters = !showFilters">
            <md-icon>filter_list</md-icon>
        </md-button>
        <span class="filter-label">{{ title }}</span>

        <div v-if="showFilters" class="filter-dropdown">
            <div class="filter-header">
                <h4>{{ title }}</h4>
                <md-button class="md-simple md-just-icon" @click="closeFilter">
                    <md-icon>close</md-icon>
                </md-button>
            </div>

            <div class="filter-body">
                <div class="filter-grid" v-if="dateRangeFilters.length > 0">
                    <component
                        v-for="(dateRangeFilter, index) in dateRangeFilters"
                        :key="index"
                        :is="dateRangeFilter.component"
                        :id="dateRangeFilter.id"
                        :default-value="dateRangeFilter.defaultValue"
                        :label="dateRangeFilter.label"
                        @change="handleFilterChange"
                        :current-filter-value="getCurrentFilterValue(dateRangeFilter)"
                        ref="filterComponents"
                    ></component>
                </div>

                <div class="filter-grid" v-if="standardFilters.length > 0">
                    <component
                        v-for="(standardFilter, index) in standardFilters"
                        :key="index"
                        :is="standardFilter.component"
                        :id="standardFilter.id"
                        :title="standardFilter.title"
                        :label="standardFilter.label"
                        :placeholder="standardFilter.placeholder"
                        :value="standardFilter.defaultValue"
                        :options="standardFilter.options"
                        @change="handleFilterChange"
                        :current-filter-value="getCurrentFilterValue(standardFilter)"
                        ref="filterComponents"
                    ></component>
                </div>

                <div class="checkbox-filters-section" v-if="checkboxFilters.length > 0">
                    <component
                        v-for="(checkboxFilter, index) in checkboxFilters"
                        :key="index"
                        :is="checkboxFilter.component"
                        :id="checkboxFilter.id"
                        :label="checkboxFilter.label"
                        :value="checkboxFilter.defaultValue"
                        @change="handleFilterChange"
                        :current-filter-value="getCurrentFilterValue(checkboxFilter)"
                        ref="filterComponents"
                    ></component>
                </div>
            </div>

            <div class="filter-footer">
                <md-button class="md-default" @click="resetFilters">Reset</md-button>
                <md-button class="md-primary" @click="applyFilters">Apply</md-button>
            </div>
        </div>
    </div>
</template>

<script>
import { DateRangeFilter, SelectFilter, TextFilter, CheckboxFilter } from './Components';

export default {
    name: 'FilterContainer',
    components: {
        DateRangeFilter,
        SelectFilter,
        TextFilter,
        CheckboxFilter
    },
    props: {
        title: {
            type: String,
            default: 'Filter'
        },
        // This is the custom filters created on the page we want to filter data.
        // This holds all the custom data, date range, etc.
        filters: {
            type: Array,
            required: true,
            validator: (value) => {
                // Each filter must have an id, component, and title
                return value.every((filter) => filter.id && filter.component);
            }
        },
        // This is the fitler object we send back to the controller.
        filter: {
            type: Object,
            default: () => {}
        }
    },
    data() {
        return {
            showFilters: false,
            activeFilters: [],
            defaultFilterState: {}
        };
    },
    computed: {
        // Separate checkbox and dateRage filters from standard filters this is so checkboxes always sit at bottom, and dateragne fitlers will sit side by side.
        standardFilters() {
            return this.activeFilters.filter(
                (filter) => filter.component !== 'CheckboxFilter' && filter.component !== 'DateRangeFilter'
            );
        },
        checkboxFilters() {
            return this.activeFilters.filter((filter) => filter.component === 'CheckboxFilter');
        },
        dateRangeFilters() {
            return this.activeFilters.filter((filter) => filter.component === 'DateRangeFilter');
        }
    },
    mounted() {
        this.defaultFilterState = { ...this.filter };
        this.initializeFilters();
    },
    methods: {
        initializeFilters() {
            this.activeFilters = this.filters;
        },
        handleFilterChange(change) {
            const filterKey = Object.keys(this.filter).find((key) => key === change.id);

            if (filterKey) {
                // Handle direct value updates
                this.filter[filterKey] = change.value;
                this.updateFilter(change.id, change.value, change.component);
            }
        },
        resetFilters() {
            // Reset all filter components to their default values
            if (this.$refs.filterComponents) {
                this.$refs.filterComponents.forEach((component) => {
                    if (typeof component.reset === 'function') {
                        component.reset();
                    }
                });
            }
        },
        updateFilter(id, value, component) {
            // eslint-disable-next-line array-callback-return, consistent-return
            this.activeFilters = this.activeFilters.flatMap((filter) => {
                if (filter.id === id) {
                    if (component === 'CheckboxFilter') {
                        return {
                            ...filter,
                            defaultValue: Boolean(value)
                        };
                    }

                    return {
                        ...filter,
                        defaultValue: String(value)
                    };
                }
                return filter;
            });
        },
        applyFilters() {
            this.defaultFilterState = { ...this.filter };
            this.$emit('filter', this.filter);
            this.showFilters = false;
        },
        closeFilter() {
            this.resetFilters();
            this.showFilters = false;
        },
        getCurrentFilterValue(component) {
            const componentFilter = Object.keys(this.defaultFilterState).find((key) => key === component.id);
            if (componentFilter) {
                return this.defaultFilterState[componentFilter];
            }

            return null;
        }
    }
};
</script>

<style scoped>
.filter-container {
    position: relative;
    display: flex;
    align-items: center;
}

.filter-label {
    margin-left: 5px;
    font-weight: 500;
}

.filter-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 10;
    background-color: white;
    border-radius: 4px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    margin-top: 10px;
    width: 600px;
    max-width: 90vw;
}

.filter-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0px 5px 0px 15px;
    border-bottom: 1px solid #eee;
}

.filter-header h4 {
    margin: 0;
}

.filter-body {
    padding: 15px;
}

.filter-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
}

.filter-footer {
    display: flex;
    justify-content: flex-end;
    padding: 5px;
    border-top: 1px solid #eee;
}

.filter-footer button {
    margin-left: 10px;
}

/* Responsive styles */
@media (max-width: 768px) {
    .filter-dropdown {
        width: 90vw;
    }

    .filter-grid {
        grid-template-columns: 1fr;
    }
}

.checkbox-filters-section {
    margin-top: 20px;
    padding-top: 15px;
    border-top: 1px solid #eee;
}
</style>
